/**
 * MaintenanceScreen — the full-platform takeover shown while maintenance is
 * ACTIVE. This is the only thing a locked-out user sees, so it carries the
 * whole brand: warm paper ground, one orange accent, hand-drawn motion.
 *
 * Design notes
 * ------------
 * - Double-bezel: the card is an outer paper tray (hairline + padding) holding
 *   an inner core with concentric radii — it reads as a physical object, not a
 *   div on a background.
 * - The hero is a pencil-sketch orbit that DRAWS ITSELF (stroke-dashoffset)
 *   then breathes; no spinner, no emoji, no clip-art.
 * - Motion is transform/opacity only (GPU-safe) on a custom cubic-bezier, and
 *   collapses to a static render under ``prefers-reduced-motion``.
 */

import type { ReactElement } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowUpRight, Clock } from "lucide-react";
import { Trans, useLingui } from "@lingui/react/macro";

import { useCountdown, type MaintenanceRecord } from "./useMaintenance";

// Heavy, physical easing — never linear/ease-in-out.
const EASE = [0.32, 0.72, 0, 1] as const;

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.09, delayChildren: 0.05 } },
};

const rise = {
  hidden: { opacity: 0, y: 24, filter: "blur(6px)" },
  show: {
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: { duration: 0.85, ease: EASE },
  },
};

/** Paper grain — fixed + pointer-events-none so it never repaints on scroll. */
function Grain() {
  return (
    <div
      aria-hidden
      className="pointer-events-none fixed inset-0 opacity-[0.035] mix-blend-multiply"
      style={{
        backgroundImage:
          "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='140' height='140'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='3'/%3E%3C/filter%3E%3Crect width='140' height='140' filter='url(%23n)'/%3E%3C/svg%3E\")",
      }}
    />
  );
}

/** Sketch orbit: strokes draw themselves, then the system breathes. */
function SketchOrbit({ still }: { still: boolean }) {
  return (
    <svg
      viewBox="0 0 200 200"
      className="h-32 w-32"
      fill="none"
      strokeLinecap="round"
      aria-hidden
    >
      <title>Ando</title>
      {[
        { r: 78, dash: 490, dur: 1.8, delay: 0.1, opacity: 0.28, spin: 34 },
        { r: 56, dash: 352, dur: 1.6, delay: 0.28, opacity: 0.45, spin: -24 },
      ].map((ring) => (
        <motion.circle
          key={ring.r}
          cx="100"
          cy="100"
          r={ring.r}
          stroke="currentColor"
          strokeWidth="1.25"
          strokeDasharray={`${ring.dash * 0.62} ${ring.dash}`}
          className="text-ink-3"
          style={{ opacity: ring.opacity, transformOrigin: "100px 100px" }}
          initial={still ? false : { strokeDashoffset: ring.dash, rotate: 0 }}
          animate={
            still
              ? { strokeDashoffset: 0 }
              : { strokeDashoffset: 0, rotate: 360 }
          }
          transition={
            still
              ? { duration: 0 }
              : {
                  strokeDashoffset: { duration: ring.dur, ease: EASE, delay: ring.delay },
                  rotate: { duration: Math.abs(ring.spin), ease: "linear", repeat: Infinity },
                }
          }
        />
      ))}

      {/* The accent core — the single orange moment on the page. */}
      <motion.circle
        cx="100"
        cy="100"
        r="11"
        className="fill-accent"
        initial={still ? false : { scale: 0.4, opacity: 0 }}
        animate={
          still
            ? { scale: 1, opacity: 1 }
            : { scale: [0.95, 1.06, 0.95], opacity: 1 }
        }
        style={{ transformOrigin: "100px 100px" }}
        transition={
          still
            ? { duration: 0 }
            : {
                opacity: { duration: 0.5, delay: 0.5 },
                scale: { duration: 3.4, ease: "easeInOut", repeat: Infinity, delay: 0.5 },
              }
        }
      />
    </svg>
  );
}

function CountdownRow({ endsAt }: { endsAt: string | null }) {
  const remaining = useCountdown(endsAt);
  if (!remaining) return null;

  const pad = (n: number) => String(n).padStart(2, "0");
  const parts: Array<[string, number]> = [
    ["h", remaining.hours],
    ["m", remaining.minutes],
    ["s", remaining.seconds],
  ];

  return (
    <motion.div variants={rise} className="flex items-center justify-center gap-2">
      <Clock size={13} className="text-ink-3" aria-hidden />
      <span className="text-[11px] text-ink-3 uppercase tracking-[0.18em]">
        <Trans>Stimato</Trans>
      </span>
      <div className="flex items-baseline gap-1 font-medium text-ink-1 tabular-nums">
        {parts.map(([unit, value], i) => (
          <span key={unit} className="flex items-baseline">
            <span className="text-[15px]">{pad(value)}</span>
            <span className="text-[10px] text-ink-3">{unit}</span>
            {i < parts.length - 1 ? (
              <span className="px-0.5 text-ink-3">:</span>
            ) : null}
          </span>
        ))}
      </div>
    </motion.div>
  );
}

export interface MaintenanceScreenProps {
  readonly record: MaintenanceRecord;
}

export function MaintenanceScreen({
  record,
}: MaintenanceScreenProps): ReactElement {
  const { t } = useLingui();
  const still = useReducedMotion() ?? false;

  return (
    <main
      // role=alert: this is the urgent, blocking state (WCAG 4.1.3).
      role="alert"
      aria-live="assertive"
      className="relative flex min-h-[100dvh] items-center justify-center overflow-hidden bg-cream px-4 py-24"
    >
      <Grain />

      {/* Ambient warmth behind the card — no harsh shadows anywhere. */}
      <div
        aria-hidden
        className="pointer-events-none absolute top-1/2 left-1/2 h-[42rem] w-[42rem] -translate-x-1/2 -translate-y-1/2 rounded-full bg-ando-orange-soft/40 blur-[120px]"
      />

      <motion.div
        variants={container}
        initial="hidden"
        animate="show"
        className="relative w-full max-w-xl"
      >
        {/* Outer tray */}
        <div className="rounded-[2rem] border border-hairline/70 bg-card/40 p-2 backdrop-blur-sm">
          {/* Inner core — concentric radius, inset highlight instead of shadow */}
          <div className="rounded-[calc(2rem-0.5rem)] border border-hairline/60 bg-card px-8 py-14 text-center shadow-[inset_0_1px_0_rgba(255,255,255,0.6)] sm:px-14">
            <motion.div variants={rise} className="flex justify-center">
              <SketchOrbit still={still} />
            </motion.div>

            <motion.div variants={rise} className="mt-10 flex justify-center">
              <span className="rounded-full border border-hairline bg-cream px-3 py-1 font-medium text-[10px] text-ink-3 uppercase tracking-[0.2em]">
                <Trans>Manutenzione</Trans>
              </span>
            </motion.div>

            <motion.h1
              variants={rise}
              className="mt-6 text-balance font-semibold text-[30px] text-ink-1 leading-[1.15] tracking-[-0.02em] sm:text-[38px]"
            >
              <Trans>Torniamo tra pochissimo</Trans>
            </motion.h1>

            <motion.p
              variants={rise}
              className="mx-auto mt-4 max-w-md text-pretty text-[14px] text-ink-2 leading-relaxed"
            >
              {record.message?.trim() ? (
                record.message
              ) : (
                <Trans>
                  Stiamo aggiornando Ando per renderlo più veloce e sicuro. Il
                  tuo lavoro è al sicuro: nessun dato viene toccato.
                </Trans>
              )}
            </motion.p>

            <div className="mt-8 space-y-5">
              <CountdownRow endsAt={record.ends_at} />

              {record.status_page_url ? (
                <motion.div variants={rise} className="flex justify-center">
                  <a
                    href={record.status_page_url}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={t`Apri la pagina di stato`}
                    className="group flex items-center gap-3 rounded-full bg-ink-1 py-2 pr-2 pl-6 font-medium text-[13px] text-cream transition-transform duration-500 ease-[cubic-bezier(0.32,0.72,0,1)] active:scale-[0.98]"
                  >
                    <Trans>Stato del servizio</Trans>
                    {/* Button-in-button: the icon lives in its own circle. */}
                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-cream/15 transition-transform duration-500 ease-[cubic-bezier(0.32,0.72,0,1)] group-hover:-translate-y-px group-hover:translate-x-0.5 group-hover:scale-105">
                      <ArrowUpRight size={14} aria-hidden />
                    </span>
                  </a>
                </motion.div>
              ) : null}
            </div>
          </div>
        </div>

        <motion.p
          variants={rise}
          className="mt-6 text-center text-[11px] text-ink-3"
        >
          <Trans>Questa pagina si aggiorna da sola quando torniamo online.</Trans>
        </motion.p>
      </motion.div>
    </main>
  );
}
